/**
 * Pure field-placement helpers for the document-sign editor.
 *
 * Extracted from FieldsEditor so the placement geometry and the drag-drop
 * payload validation can be unit-tested without a rendered PDF page (the
 * editor itself needs pdf.js + an IntersectionObserver to render).
 */

import type { FieldType } from './FieldPalette';

/**
 * Default placement size for each field type, as a fraction of the page.
 * `w`/`h` are in [0,1] — checkbox-style fields are small squares, text-
 * collection fields are wider.
 *
 * Heights were reduced to 75% of their original values per UX feedback —
 * the previous defaults were too tall relative to the surrounding document
 * text. Widths unchanged. Existing fields keep their stored h; this only
 * affects new placements.
 */
export const DEFAULT_FIELD_FRAC: Record<FieldType, { w: number; h: number }> = {
  signature: { w: 0.25, h: 0.0375 }, // 0.05 × 0.75
  initial: { w: 0.08, h: 0.0375 }, // 0.05 × 0.75
  date_signed: { w: 0.15, h: 0.03 }, // 0.04 × 0.75
  signer_name: { w: 0.2, h: 0.03 }, // 0.04 × 0.75
  email: { w: 0.2, h: 0.03 }, // 0.04 × 0.75
  company: { w: 0.2, h: 0.03 }, // 0.04 × 0.75
  title: { w: 0.2, h: 0.03 }, // 0.04 × 0.75
  text: { w: 0.2, h: 0.03 }, // 0.04 × 0.75
  number: { w: 0.12, h: 0.03 }, // 0.04 × 0.75
  checkbox: { w: 0.04, h: 0.03 }, // 0.04 × 0.75
};

/**
 * Centre a field of size `def` on the (xFrac, yFrac) page point the user
 * clicked or dropped, then clamp it so the whole field stays inside the
 * [0,1] page. Returns the field's top-left corner plus its size, all as
 * page-fractions.
 */
export function clampFieldPlacement(
  xFrac: number,
  yFrac: number,
  def: { w: number; h: number },
): { x: number; y: number; w: number; h: number } {
  const x = Math.max(0, Math.min(1 - def.w, xFrac - def.w / 2));
  const y = Math.max(0, Math.min(1 - def.h, yFrac - def.h / 2));
  return { x, y, w: def.w, h: def.h };
}

/**
 * Type guard for a drag-drop payload string: true only for a known field
 * type. `hasOwnProperty` (not `in`) so prototype keys like `toString` can
 * never be mistaken for a placeable type.
 */
export function isPlaceableFieldType(value: string): value is FieldType {
  return Object.prototype.hasOwnProperty.call(DEFAULT_FIELD_FRAC, value);
}
